import { service } from '@ember/service';
import Component from '@glimmer/component';
import CurrencySuperscriptCents from '../../currency-superscript-cents.gts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import type { TASAssistanceHistoryType } from '../../../types/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';

export interface ItemPaymentSignature {
  amount?: number;
  date?: string;
  paymentType?: TASAssistanceHistoryType;
}

export default class ItemPayment extends Component<ItemPaymentSignature> {
  @service declare intl: IntlService;

  get isRepaymentRequest() {
    return ['REPAYMENT_AMOUNT_UPDATED', 'REPAYMENT_REQUEST'].includes(this.args.paymentType || '');
  }

  get isDeclinedOrDisputed() {
    return ['CARD_DECLINED', 'CARD_DISPUTED'].includes(this.args.paymentType || '');
  }

  get paymentColor() {
    if (this.isRepaymentRequest) return 'text-yellow-700';
    if (this.isDeclinedOrDisputed) return 'text-red-700';
    if (this.args.paymentType === 'PAYMENT') return 'text-green-700';
    return 'text-tio-gray-700';
    // TODO: different color for forgiven?
  }

  get paymentString() {
    return this.intl.t(`tuition_assistance.applications.history.payments.${this.args.paymentType}`);
  }

  get displayAmount() {
    return Math.abs(this.args.amount || 0);
  }

  <template>
    <div class="tio-col-flush ml-2 mb-2 w-full">
      <p class="py-0.5 text-tio-gray-700">
        <span class="{{this.paymentColor}} font-semibold">
          <CurrencySuperscriptCents @amountCents={{this.displayAmount}} />
        </span>
        <span>{{this.paymentString}}</span>
      </p>
      <div
        class="flex flex-wrap md:flex-nowrap md:whitespace-nowrap order-1 md:order-none md:divide-x md:divide-gray-400"
      >
        <time datetime={{@date}} class="py-0.5 px-2 text-xs leading-5 text-tio-gray-400">
          {{safeFormatDate @date hour="numeric" minute="2-digit" timeZoneName="short"}}
        </time>
        <time datetime={{@date}} class="py-0.5 px-2 text-sm leading-5 text-tio-gray-600">
          {{safeFormatDate @date month="short" day="2-digit" year="numeric"}}
        </time>
      </div>
    </div>
  </template>
}

/**
 * Example usage:
 *
 *
  get seedPayments() {
    return [
      {
        paymentState: 'Paid',
        amount: 6000, // $60.00
        date: '2024-10-20',
      },
      {
        paymentState: 'Repayable',
        amount: -4500, // $45.00
        date: '2024-09-15',
      },
      {
        paymentState: 'Unpaid',
        amount: -1500, // $75.00
        date: '2024-08-05',
      },
      {
        paymentState: 'Paid',
        amount: 12000, // $120.00
        date: '2024-07-25',
      },
      {
        paymentState: 'Unpaid',
        amount: -3000, // $30.00
        date: '2024-06-10',
      },
    ];
  }

  <template>
          <Feed @feed={{this.timeline}}>
            <:record as |item|>
              <ItemDetails @details={{item.details}} @date={{item.date}} />
            </:record>
            <:comment as |item|>
              <ItemComment
                @approverComment={{item.approverComment}}
                @approverName={{item.approverName}}
              />
            </:comment>
          </Feed>

          <CardGeneric>
            <:header>
              <h2 class="tio-text-heading">Payment Details</h2>
            </:header>
            <:body>
              <Feed @feed={{this.seedPayments}}>
                <:record as |item|>
                  <ItemPayment
                    @amount={{item.amount}}
                    @date={{item.date}}
                    @paymentState={{item.paymentState}}
                  />
                </:record>
              </Feed>
              <Divider class="py-2" />
            </:body>
          </CardGeneric>
  </template>
          */
